/* istanbul ignore file */
import { useIntl } from 'react-intl';
import {
  Box,
  Checkbox,
  Divider,
  Stack,
  Typography,
  Image,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  InlineNotification,
  useI18n,
} from '@packages/shared';
import { useState, useEffect, useRef } from 'react';
import { ArrowMediumDown32, Checkmark32 } from '@packages/themes/icons';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { useAuthMutation } from '@packages/gql';
import { ToggleServiceProductDocument } from '@packages/gql/generated/shopping/ToggleServiceProductDocument';
import type { AplusFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import { serviceItemsMessages } from './messages';

export const ServiceItems = ({
  aplusData,
  pli,
}: {
  aplusData: FragmentType<typeof AplusFragmentFragmentDoc>;
  pli: string;
}) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('ATB');
  const { locale } = useI18n();
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] = useState(false);
  const refTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fragmentData = getFragmentData(AplusFragmentFragmentDoc, aplusData);
  const { clusters } = fragmentData || {};
  const [mutationState, toggleServiceProduct] = useAuthMutation(ToggleServiceProductDocument);
  const [state, setState] = useState<AplusFragmentFragment['clusters']>(clusters);

  const onSelectItem = async (serviceUuid: string) => {
    try {
      setLoadingItem(serviceUuid);
      if (!mutationState.fetching) {
        const result = await toggleServiceProduct({ locale, lineItemId: pli, serviceUuid });
        if (result && result.data) {
          const basketFragment = getFragmentData(
            BasketFragmentFragmentDoc,
            result.data.toggleServiceProduct,
          );
          basketFragment.lineItems.forEach((lineItem) => {
            const lineItemFragment = getFragmentData(LineItemFragmentFragmentDoc, lineItem);
            if (lineItemFragment.id === pli) {
              const aplusFragment = getFragmentData(
                AplusFragmentFragmentDoc,
                lineItemFragment.aplus,
              );
              setState(aplusFragment!.clusters);
            }
          });
        }
      }
    } catch {
      setErrorNotification(true);
      refTimer.current = setTimeout(() => {
        setErrorNotification(false);
      }, 5000);
    } finally {
      setLoadingItem(null);
    }
  };

  useEffect(
    () => () => {
      if (refTimer.current !== null) {
        window.clearTimeout(refTimer.current);
      }
    },
    [],
  );

  return (
    <>
      {errorNotification && (
        <InlineNotification severity="error">
          {formatMessage(serviceItemsMessages.serviceItemsErrorMessage)}
        </InlineNotification>
      )}
      <Accordion
        defaultExpanded
        sx={{ boxShadow: 0, '&.Mui-expanded': { ':before': { opacity: 1 } } }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ArrowMediumDown32 />}
          aria-controls="serviceitems-content"
          id="serviceitems-header"
          sx={{ p: 0 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              sx={{
                display: 'flex',
                color: 'common.white',
                height: 16,
                typography: 'body3',
                textTransform: 'uppercase',
              }}
              label={formatMessage(serviceItemsMessages.chipLabel)}
              size="small"
              color="success"
            />
            <Typography variant="body2">
              {formatMessage(serviceItemsMessages.serviceItemsHeadline)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={1} divider={<Divider />}>
            {state.map(({ label, services }) => (
              <Stack key={label} gap={2}>
                <Typography
                  variant="body3"
                  fontWeight="fontWeightSemiBold"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {label}
                </Typography>
                {services.map(({ uuid, name, price, serviceIcon, included, selected }) => (
                  <Stack key={uuid} direction="row" alignItems="flex-start">
                    <Box
                      sx={{ width: '2.5rem', height: '1.75rem' }}
                      display="flex"
                      alignItems="center"
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {included ? (
                        <Checkmark32 fontSize="medium" color="success" />
                      ) : loadingItem === uuid ? (
                        <CircularProgress size="s" />
                      ) : (
                        <Checkbox
                          sx={{ padding: 0 }}
                          checked={selected || false}
                          onChange={() => onSelectItem(uuid)}
                        />
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      gap={2}
                      alignItems="flex-start"
                      flex={1}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Box>
                        <Image src={`${serviceIcon}.svg`} alt={name!} width={30} height={30} />
                      </Box>
                      <Stack flex={1}>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography data-testid="service-name" variant="body2">
                            {name}
                          </Typography>
                          {included ? (
                            <Typography
                              variant="body2"
                              fontWeight="fontWeightSemiBold"
                              sx={{ color: 'success.main' }}
                            >
                              {formatMessage(serviceItemsMessages.serviceItemsInclusive)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" fontWeight="fontWeightSemiBold">
                              +{formatPrice(price?.value || 0, price?.currency)}
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="body3" sx={{ color: 'text.darkTransparent' }}>
                          {formatMessage(serviceItemsMessages.serviceItemsMoreInfo)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
